import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface ProcessStep {
  number: string;
  title: string;
  description: string;
  icon: string;
  link?: {
    text: string;
    href: string;
  };
}

const ProcessSection = () => {
  const steps: ProcessStep[] = [
    {
      number: '01',
      title: 'Online diagnóza',
      description: 'Vyplníte krátký dotazník, který nám pomůže pochopit vaši situaci a připravit se na první schůzku.',
      icon: 'DocumentTextIcon',
      link: {
        text: 'Začít diagnózu',
        href: '/online-initial-diagnosis',
      },
    },
    {
      number: '02',
      title: 'První schůzka',
      description: 'Osobně nebo online probereme vaši situaci, cíle a vytvoříme základ vašeho finančního plánu.',
      icon: 'UserGroupIcon',
      link: {
        text: 'Co očekávat',
        href: '/first-meeting-analysis',
      },
    },
    {
      number: '03',
      title: 'Vytvoření plánu',
      description: 'Připravíme komplexní finanční plán přizpůsobený vašim potřebám a životní situaci.',
      icon: 'DocumentChartBarIcon',
    },
    {
      number: '04',
      title: 'Implementace',
      description: 'Společně začneme realizovat dohodnutou strategii a nastavíme všechny potřebné procesy.',
      icon: 'CogIcon',
    },
    {
      number: '05',
      title: 'Pravidelné revize',
      description: 'Setkáváme se pravidelně, vyhodnocujeme pokrok a upravujeme plán podle aktuální situace.',
      icon: 'ArrowPathIcon',
      link: {
        text: 'Dlouhodobá spolupráce',
        href: '/service-long-term-partnership',
      },
    },
  ];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4 font-headline">
              Jak začít s živým finančním plánem
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Pět jednoduchých kroků od první diagnózy po dlouhodobé partnerství
            </p>
          </div>

          <div className="relative">
            {/* Connection Line - Desktop */}
            <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-border"></div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-4">
              {steps.map((step, index) => (
                <div key={index} className="relative">
                  <div className="bg-card rounded-xl p-6 shadow-card hover:shadow-elevated transition-smooth border border-border h-full flex flex-col">
                    <div className="flex items-start space-x-4 lg:flex-col lg:space-x-0 lg:items-center lg:text-center mb-4">
                      <div className="relative z-10">
                        <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mb-3 shadow-card">
                          <Icon name={step.icon as any} size={28} className="text-white" />
                        </div>
                        <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-accent flex items-center justify-center text-accent-foreground text-xs font-bold">
                          {step.number}
                        </div>
                      </div>
                      <div className="flex-1 lg:flex-none">
                        <h3 className="text-lg font-semibold text-text-primary mb-2">
                          {step.title}
                        </h3>
                      </div>
                    </div>
                    <p className="text-sm text-text-secondary leading-relaxed mb-4 flex-1">
                      {step.description}
                    </p>
                    {step.link && (
                      <Link
                        href={step.link.href}
                        className="inline-flex items-center space-x-2 text-sm font-medium text-primary hover:text-primary/80 transition-smooth mt-auto"
                      >
                        <span>{step.link.text}</span>
                        <Icon name="ArrowRightIcon" size={16} className="text-current" />
                      </Link>
                    )}
                  </div>

                  {/* Mobile Connection Line */}
                  {index < steps.length - 1 && (
                    <div className="lg:hidden flex justify-center my-4">
                      <Icon name="ChevronDownIcon" size={24} className="text-border" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-12 text-center">
            <Link
              href="/how-we-work"
              className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-smooth font-medium"
            >
              <span>Zjistit více o tom, jak pracujeme</span>
              <Icon name="ArrowRightIcon" size={20} className="text-current" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;